type RoutesConfig = {
  login: string;
  logout: string;
  refresh: string;
  signUp: {
    root: string;
    signupForm: string;
    validateUser: string;
    signupSuccess: string;
    signupSSO: string;
  };
  activate: string;
  notifications: string;
  resetPassword: {
    root: string;
    request: string;
    confirm: string;
    emailSent: string;
  };
  home: string;
  main: string;
  organisations: { all: string; organisation: string };
  returns: { root: string; add: string; edit: string; tifdAdd: string; tifdEdit: string };
  account: { root: string; details: string; error: string };
  users: string;
  notFound: string;
  termsOfUse: string;
  privacyPolicy: string;
  accessibility: string;
  accessibilityStatement: string;
  tempRedirector: string;
  vat: {
    root: string;
    viewReportLink: string;
    report: string;
    tifdReport: string;
    tifdViewReportLink: string;
  };
  billing: {
    root: string;
  };
  pricing: {
    root: string;
    checkout: string;
  };
  vies: {
    qualifiedEntities: string;
    customersAndSuppliers: string;
    contact: string;
  };
  support: string;
  whatsNew: string;
  reporting: string;
  pictureTest: string;
  submissions: string;
  planner: string;
  oneDriveCallback: string;
  dev: {
    buttons: string;
    designSystem: {
      root: string;
      buttons: string;
      forms: string;
      dataDisplay: string;
      feedback: string;
      overlays: string;
      layout: string;
      patterns: string;
      tables: string;
      ai: string;
      providers: string;
      listsCards: string;
      integrations: string;
      dataRetrieval: string;
      icons: string;
      utilities: string;
      featureDots: string;
      navigation: string;
      workflows: string;
      submissions: string;
      dashboard: string;
      vatReturns: string;
      mapping: string;
      permissions: string;
      auth: string;
      billing: string;
      antdTheme: string;
    };
  };
};

const routes: RoutesConfig = {
  login: '/login',
  logout: '/logout',
  refresh: '/refresh',
  signUp: {
    root: '/signup',
    signupForm: '/signup/form',
    validateUser: '/signup/validate/:id',
    signupSuccess: '/signup/success',
    signupSSO: '/signup/sso',
  },
  activate: '/activate',
  notifications: '/notifications',
  resetPassword: {
    root: '/forgot-password',
    request: '/forgot-password/request',
    confirm: '/forgot-password/confirm/:id',
    emailSent: '/forgot-password/email-sent',
  },
  home: '/',
  main: '/main',
  organisations: {
    all: '/entities',
    organisation: '/entities/organisation/:id',
  },
  returns: {
    root: '/returns',
    add: '/returns/:connectionId',
    edit: '/returns/:connectionId/editMapping/:mappingId',
    tifdAdd: '/returns/:connectionId/tifdMapping',
    tifdEdit: '/returns/:connectionId/tifdMapping/:mappingId',
  },
  account: {
    root: '/profile',
    details: '/profile/:accountId',
    error: '/profile/error/:errorId',
  },
  users: '/users',
  notFound: '/not-found',
  termsOfUse: '/terms-of-use',
  privacyPolicy: '/privacy-policy',
  accessibility: '/accessibility',
  accessibilityStatement: '/accessibility-statement',
  tempRedirector: '/redirector/:route',
  vat: {
    root: '/vat',
    viewReportLink:
      '/vat/report/connection/:connectionId/mapping/:mappingId/log/:dataLogId',
    report: '/vat/report',
    tifdReport: '/vat/tifdReport',
    tifdViewReportLink:
      '/vat/tifdReport/connection/:connectionId/mapping/:mappingId/log/:dataLogId',
  },
  billing: {
    root: '/billing',
  },
  pricing: {
    root: '/pricing',
    checkout: '/pricing/checkout/:licenseId',
  },
  vies: {
    qualifiedEntities: '/vat-checker',
    customersAndSuppliers: '/vat-checker/customers-and-suppliers/:connectionId',
    contact:
      '/vat-checker/contact-details/connection/:connectionId/contact/:contactId',
  },
  support: '/support',
  whatsNew: '/whatsNew',
  reporting: '/reporting',
  pictureTest: '/picture-test',
  submissions: '/submissions',
  planner: '/planner',
  oneDriveCallback: '/auth-callback',
  dev: {
    buttons: '/dev/buttons',
    designSystem: {
      root: '/dev/design-system',
      buttons: '/dev/design-system/buttons',
      forms: '/dev/design-system/forms',
      dataDisplay: '/dev/design-system/data-display',
      feedback: '/dev/design-system/feedback',
      overlays: '/dev/design-system/overlays',
      layout: '/dev/design-system/layout',
      patterns: '/dev/design-system/patterns',
      tables: '/dev/design-system/tables',
      ai: '/dev/design-system/ai',
      providers: '/dev/design-system/providers',
      listsCards: '/dev/design-system/lists-cards',
      integrations: '/dev/design-system/integrations',
      dataRetrieval: '/dev/design-system/data-retrieval',
      icons: '/dev/design-system/icons',
      utilities: '/dev/design-system/utilities',
      featureDots: '/dev/design-system/feature-dots',
      navigation: '/dev/design-system/navigation',
      workflows: '/dev/design-system/workflows',
      submissions: '/dev/design-system/submissions',
      dashboard: '/dev/design-system/dashboard',
      vatReturns: '/dev/design-system/vat-returns',
      mapping: '/dev/design-system/mapping',
      permissions: '/dev/design-system/permissions',
      auth: '/dev/design-system/auth',
      billing: '/dev/design-system/billing',
      antdTheme: '/dev/design-system/antd-theme',
    },
  },
};

export const getRoute = (
  path: string,
  params?: { [key: string]: string | number },
  routesConfig: RoutesConfig = routes
): RoutesConfig =>
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  path.split('.').reduce((routeBranch: any, pathItem: string) => {
    if (routeBranch && routeBranch[pathItem]) {
      const route = routeBranch[pathItem];
      if (typeof route === 'string') {
        if (!params || typeof params === 'undefined') {
          return route;
        }

        return Object.entries(params).reduce((replaced, [key, value]) => {
          return replaced.replace(`:${key}`, String(value));
        }, route);
      }
      return routeBranch[pathItem];
    }
  }, routesConfig);

export default routes;
